import React from "react";
import { InfoIcon, ShieldAlertIcon } from "lucide-react";

import SupportInfo from "./SupportInfo";
import { ResultButton } from "./ResultButton";

type Props = {
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  children?: React.ReactNode;
  showGoHome?: boolean;
  showSupport?: boolean;
  showSupportForError?: boolean;
};

export type ResultBlockerProps = Props & {
  status: "notFound" | "unauthorized" | "internalError" | "offline";
};

const defaultPropsByStatus: { [key in ResultBlockerProps["status"]]: Props } = {
  notFound: {
    title: "No se puede mostrar la página",
    subTitle: "Disculpa, la página que quieres acceder no existe.",
    showGoHome: true,
  },
  unauthorized: {
    title: "No se puede mostrar la página",
    subTitle: "Disculpa, no tienes permisos suficientes para acceder a este módulo.",
    showGoHome: false,
    showSupport: true,
  },
  internalError: {
    title: "Ocurrió un problema inesperado",
    subTitle: "Disculpa, en este momento no se puede mostrar esta página.",
    showGoHome: true,
    showSupport: true,
    showSupportForError: true,
  },
  offline: {
    title: (
      <>
        No hay conexión <br /> a internet
      </>
    ),
    subTitle: "En este momento no se puede mostrar la página.",
    showGoHome: false,
    showSupport: false,
    showSupportForError: false,
  },
};

/**
 * Se utiliza para mostrar errores bloqueantes que impiden continuar con el flujo de trabajo
 *
 *  ```

 *  import { ResultBlocker } from "@/components";
 *
 *  ```
 *  ```
 
 *  <ResultBlocker status="unauthorized" />

 *  ```
 */
export function ResultBlocker({
  status,
  title,
  subTitle,
  showGoHome,
  showSupport,
  showSupportForError,
  children,
}: ResultBlockerProps) {
  const defaultProps = defaultPropsByStatus[status];

  title = title ?? defaultProps.title;
  subTitle = subTitle ?? defaultProps.subTitle;
  showGoHome = showGoHome ?? defaultProps.showGoHome;
  children = children ?? defaultProps.children;
  showSupport = showSupport ?? defaultProps.showSupport;
  showSupportForError = showSupportForError ?? defaultProps.showSupportForError;

  const Icon = status === "unauthorized" ? ShieldAlertIcon : InfoIcon;

  const handleNavigate = () => {
    window.location.href = "/";
  };

  return (
    <div className="flex h-full w-[520px] flex-col items-center justify-center gap-4 font-[Montserrat] text-[#9E9E9E]">
      <div className="flex gap-2">
        <h2 className="text-[32px] font-bold text-rdc-primary">{title}</h2>
        <Icon className="text-rdc-primary" size={70} />
      </div>
      <h3 className="text-[20px] font-normal text-[#383838]">{subTitle}</h3>
      {children && <>{children}</>}
      {showGoHome && <ResultButton onClick={handleNavigate}>Volver al inicio</ResultButton>}
      {(showSupport || showSupportForError) && <SupportInfo forError={showSupportForError} />}
    </div>
  );
}
